import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/src/lib/supabase';
import { completeLinkedTasks } from '@/src/lib/taskLinks';

export interface EodQuestion {
  key: string;
  label: string;
  placeholder: string;
}

export const DEFAULT_EOD_QUESTIONS: Record<string, EodQuestion[]> = {
  chef: [
    { key: 'kitchen_prep', label: 'Kitchen Prep', placeholder: 'How was prep today? Any issues with mise en place?' },
    { key: 'kitchen_service', label: 'Kitchen Service', placeholder: 'How did service run? Any bottlenecks or delays?' },
    { key: 'waste', label: 'Wastage', placeholder: 'Any notable food waste today?' },
    { key: 'stock_issues', label: 'Stock Issues', placeholder: 'Any items running low or needing to be ordered?' },
    { key: 'kitchen_incidents', label: 'Incidents', placeholder: 'Any incidents, injuries or equipment issues?' },
    { key: 'training', label: 'Training Notes', placeholder: 'Any training observations for kitchen staff?' },
  ],
  bartender: [
    { key: 'bar_service', label: 'Bar Service', placeholder: 'How did bar service run tonight?' },
    { key: 'bar_stock', label: 'Stock & Wastage', placeholder: 'Any stock running low? Any wastage to flag?' },
    { key: 'popular_items', label: 'Popular Items', placeholder: 'What was selling well tonight?' },
    { key: 'guest_feedback', label: 'Guest Feedback', placeholder: 'Any notable guest feedback about drinks?' },
    { key: 'bar_incidents', label: 'Incidents', placeholder: 'Any incidents or issues at the bar?' },
    { key: 'training', label: 'Training Notes', placeholder: 'Any training observations for bar staff?' },
  ],
  staff: [
    { key: 'guest_experience', label: 'Guest Experience', placeholder: 'How was the overall guest experience tonight?' },
    { key: 'guest_complaints', label: 'Guest Complaints / Issues', placeholder: 'Any complaints or issues to flag?' },
    { key: 'above_and_beyond', label: 'Going Above & Beyond', placeholder: 'Any staff moments worth highlighting?' },
    { key: 'kitchen', label: 'Kitchen', placeholder: 'Kitchen notes — prep, service, any issues?' },
    { key: 'bar', label: 'Bar', placeholder: 'Bar notes — service, stock, any issues?' },
    { key: 'training', label: 'Training', placeholder: 'Any training observations or needs?' },
    { key: 'events', label: 'Happy Hour / Events', placeholder: 'Event or happy hour notes?' },
  ],
};

// manager/owner use the same questions as staff
DEFAULT_EOD_QUESTIONS.manager = DEFAULT_EOD_QUESTIONS.staff;
DEFAULT_EOD_QUESTIONS.owner = DEFAULT_EOD_QUESTIONS.staff;

export interface EodReport {
  id: string;
  venue_id: string;
  user_id: string;
  report_date: string;
  guest_experience: string | null;
  guest_complaints: string | null;
  above_and_beyond: string | null;
  kitchen: string | null;
  bar: string | null;
  wine: string | null;
  training: string | null;
  events: string | null;
  submitted_at: string;
  answers?: Record<string, string> | null;
}

export interface EodReportWithUser extends EodReport {
  user_name: string;
  user_role: string;
}

export function useEodReports(venueId: string | null, userId: string | null) {
  const [todayReport, setTodayReport] = useState<EodReport | null>(null);
  const [eodQuestions, setEodQuestions] = useState<Record<string, EodQuestion[]>>({});
  const [allReports, setAllReports] = useState<EodReportWithUser[]>([]);
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().split('T')[0];

  const loadTodayReport = useCallback(async () => {
    if (!venueId || !userId) return;
    const { data } = await supabase
      .from('eod_reports')
      .select('*')
      .eq('venue_id', venueId)
      .eq('user_id', userId)
      .eq('report_date', today)
      .maybeSingle();
    setTodayReport(data as EodReport | null);
  }, [venueId, userId, today]);

  const loadEodQuestions = useCallback(async () => {
    if (!venueId) return;
    const { data } = await supabase.from('venues').select('eod_questions').eq('id', venueId).maybeSingle();
    const saved = (data?.eod_questions as Record<string, EodQuestion[]> | null) ?? {};
    setEodQuestions(saved);
  }, [venueId]);

  const saveEodQuestions = useCallback(async (questions: Record<string, EodQuestion[]>) => {
    if (!venueId) return { error: 'no venue' };
    const { error } = await supabase.from('venues').update({ eod_questions: questions }).eq('id', venueId);
    if (!error) setEodQuestions(questions);
    return { error: error?.message ?? null };
  }, [venueId]);

  const loadAllReports = useCallback(async (date?: string) => {
    if (!venueId) return;
    const targetDate = date ?? today;
    const { data } = await supabase
      .from('eod_reports')
      .select('*, users(name, app_role)')
      .eq('venue_id', venueId)
      .eq('report_date', targetDate)
      .order('submitted_at', { ascending: true });

    const mapped = (data ?? []).map((r: any) => ({
      ...r,
      user_name: r.users?.name ?? 'Unknown',
      user_role: r.users?.app_role ?? 'staff',
    }));
    setAllReports(mapped as EodReportWithUser[]);
  }, [venueId, today]);

  useEffect(() => {
    if (!venueId || !userId) { setLoading(false); return; }
    setLoading(true);
    Promise.all([loadTodayReport(), loadAllReports(), loadEodQuestions()]).then(() => setLoading(false));
  }, [venueId, userId, loadTodayReport, loadAllReports]);

  const submitReport = useCallback(async (input: Record<string, string>) => {
    if (!venueId || !userId) return { error: 'no venue/user' };
    const { error } = await supabase
      .from('eod_reports')
      .insert({
        venue_id: venueId,
        user_id: userId,
        report_date: today,
        answers: input,
      });
    if (!error) {
      await completeLinkedTasks(userId, { type: 'eod_report' });
      await loadTodayReport();
    }
    return { error: error?.message ?? null };
  }, [venueId, userId, today, loadTodayReport]);

  return {
    todayReport,
    allReports,
    eodQuestions,
    loading,
    submitReport,
    loadAllReports,
    saveEodQuestions,
  };
}